<template>
  <v-dialog v-model="dialog" max-width="640">
    <div v-if="journey" class="vjd">
      <div class="vjd__head">
        <div>
          <div class="vjd__name">
            <span v-if="journey.visitor.leadTier" class="vjd__tier" :class="tierClass(journey.visitor.leadTier)">{{ tierLabel(journey.visitor.leadTier) }} · {{ journey.visitor.leadScore }}</span>
            <span v-if="journey.visitor.identified" class="vjd__badge">Lead</span>
            {{ journey.visitor.contactName || journey.visitor.contactEmail || 'Visitante anónimo' }}
          </div>
          <div class="vjd__meta">
            <span v-if="journey.visitor.contactEmail">{{ journey.visitor.contactEmail }}</span>
            <span v-if="journey.visitor.contactPhone"> · {{ journey.visitor.contactPhone }}</span>
          </div>
        </div>
        <v-btn icon small @click="dialog = false"><hero-icon :icon="CloseIcon" :size="18" /></v-btn>
      </div>

      <div class="vjd__chips">
        <span v-if="journey.visitor.topOperation" class="vjd__chip vjd__chip--op">{{ opLabel(journey.visitor.topOperation) }}</span>
        <span v-if="journey.visitor.topCategory" class="vjd__chip">{{ catLabel(journey.visitor.topCategory) }}</span>
        <span v-if="journey.visitor.topZone" class="vjd__chip vjd__chip--zone">{{ journey.visitor.topZone }}</span>
        <span v-if="priceLabel(journey.visitor.intent)" class="vjd__chip vjd__chip--price">{{ priceLabel(journey.visitor.intent) }}</span>
        <span v-if="journey.visitor.device" class="vjd__chip vjd__chip--dev">{{ deviceLabel(journey.visitor.device) }}</span>
      </div>

      <div class="vjd__summary">
        Primera visita {{ fmtDate(journey.visitor.firstSeen) }} ·
        {{ journey.visitor.searchesCount }} búsquedas ·
        {{ journey.visitor.visitsCount }} visitas ·
        {{ journey.visitor.inquiriesCount }} consultas
      </div>

      <!-- why this lead scores what it does — top contributing signals -->
      <div v-if="scoreReasons.length" class="vjd__why">
        <span class="vjd__whylabel">Señales</span>
        <span v-for="r in scoreReasons" :key="r.key" class="vjd__wchip">{{ r.label }} <b>+{{ r.value }}</b></span>
      </div>

      <div class="vjd__sessions">
        <div v-for="s in sessions" :key="s.index" class="vjd__session">
          <button type="button" class="vjd__shead" @click="toggle(s.index)">
            <hero-icon :icon="expanded[s.index] ? ChevronDownIcon : ChevronRightIcon" :size="16" class="vjd__schev" />
            <span class="vjd__stitle">
              {{ s.index === 1 ? "Última sesión" : "Sesión" }}
              <span class="vjd__sdate">· {{ fmtSessionSpan(s) }}</span>
            </span>
            <span v-if="s.device" class="vjd__sdev">{{ deviceLabel(s.device) }}</span>
            <span v-if="s.hasInquiry" class="vjd__sconv">Consultó</span>
            <span class="vjd__ssum">{{ sessionSummary(s) }}</span>
          </button>

          <div v-show="expanded[s.index]" class="vjd__timeline">
            <div
              v-for="(e, i) in s.events"
              :key="i"
              class="vjd__event"
              :class="['vjd__event--' + e.kind, { 'vjd__event--link': isClickable(e) }]"
              @click="openEvent(e)"
            >
              <div class="vjd__edot"></div>
              <div class="vjd__ebody">
                <div class="vjd__etop">
                  <span class="vjd__ekind">{{ kindLabel(e.kind) }}</span>
                  <span class="vjd__ewhen">{{ fmtDateTime(e.at) }}</span>
                </div>
                <template v-if="e.kind === 'search'">
                  <div v-if="searchChips(e.meta).length" class="vjd__chips vjd__chips--ev">
                    <span v-for="(c, ci) in searchChips(e.meta)" :key="ci" class="vjd__chip" :class="c.cls">{{ c.label }}</span>
                  </div>
                  <div v-else class="vjd__edetail">Todas las propiedades</div>
                </template>
                <template v-else>
                  <div class="vjd__etitle">{{ e.title }}</div>
                  <div v-if="e.kind === 'inquiry' && e.propertyAddress" class="vjd__eprop">
                    <hero-icon :icon="HomeIcon" :size="13" />
                    <span>{{ e.propertyAddress }}</span>
                  </div>
                  <div v-if="eventDetail(e)" class="vjd__edetail">{{ truncate(eventDetail(e)) }}</div>
                </template>
              </div>
              <hero-icon v-if="isClickable(e)" :icon="ChevronRightIcon" :size="16" class="vjd__echev" />
            </div>
          </div>
        </div>
        <div v-if="!sessions.length" class="vjd__empty">Sin actividad registrada.</div>
      </div>
    </div>
    <div v-else class="vjd vjd--loading">
      <v-progress-circular indeterminate color="accent" size="30" width="3" />
    </div>
  </v-dialog>
</template>

<script>
// Reusable visitor-journey drill-down: open(visitorId) fetches the summary + merged
// timeline and renders it. Clickable visit/inquiry steps emit open-property /
// open-inquiries so the host (audience explorer, inquiries list) decides how to
// surface the property. Shared by AudienceTab and the Consultas list.
import { VisitorJourney } from "@/graphql/queries/analytics";
import HeroIcon from "@/components/shared/HeroIcon";
import CloseIcon from "heroicons/24/outline/x-mark.svg";
import HomeIcon from "heroicons/24/outline/home.svg";
import ChevronRightIcon from "heroicons/24/outline/chevron-right.svg";
import ChevronDownIcon from "heroicons/24/outline/chevron-down.svg";

const OP_LABELS = { sell: "Venta", rent: "Alquiler" };
const SRC_LABELS = { search: "Desde la búsqueda", landing: "Desde el inicio", recommendation: "Desde una recomendación", direct: "Acceso directo" };
const CAT_LABELS = { living: "Vivienda", commercial: "Comercial", land: "Terrenos", parking: "Cocheras", other: "Otros" };
const DEV_LABELS = { mobile: "Móvil", tablet: "Tablet", desktop: "Escritorio", unknown: "Desconocido" };
const TIER_LABELS = { hot: "Caliente", warm: "Tibio", cold: "Frío" };
// human labels for LeadScore.breakdown components (strongest signals first for tie-breaks)
const BREAKDOWN_LABELS = {
  inquiry: "Consultó",
  abandoned_contact: "Abrió el formulario",
  favorite: "Guardó favoritos",
  repeat_visit: "Revisitó propiedades",
  distinct_property: "Exploró propiedades",
  identified: "Contacto conocido",
  active_day: "Volvió varios días",
  gallery_open: "Miró fotos",
  recommendation_visit: "Abrió recomendadas",
  specific_search: "Búsqueda específica",
  search: "Buscó",
  zero_result_search: "Búsqueda sin resultados"
};

export default {
  components: { HeroIcon },

  data: () => ({
    CloseIcon,
    HomeIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    dialog: false,
    journey: null,
    expanded: {} // session index -> open?
  }),

  computed: {
    // grouped sessions from the backend; fall back to one session wrapping the flat
    // events (keeps working if the backend hasn't shipped `sessions` yet)
    sessions() {
      if (!this.journey) return [];
      const s = this.journey.sessions;
      if (s && s.length) return s;
      const evs = this.journey.events || [];
      if (!evs.length) return [];
      return [{ index: 1, startedAt: evs[evs.length - 1].at, endedAt: evs[0].at, events: evs, searches: 0, visits: 0, inquiries: 0, hasInquiry: false }];
    },

    // top point-contributing signals behind the lead score (the "why")
    scoreReasons() {
      const b = this.journey && this.journey.visitor && this.journey.visitor.scoreBreakdown;
      if (!b) return [];
      return Object.keys(BREAKDOWN_LABELS)
        .map(k => ({ key: k, label: BREAKDOWN_LABELS[k], value: b[k] || 0 }))
        .filter(r => r.value > 0)
        .sort((a, z) => z.value - a.value)
        .slice(0, 5);
    }
  },

  methods: {
    // open by visitor (auto-expands to the person) or directly by lead
    open(visitorId, leadId = null) {
      if (!visitorId && !leadId) return;
      this.journey = null;
      this.expanded = {};
      this.dialog = true;
      this.$apollo
        .query({ query: VisitorJourney, fetchPolicy: "no-cache", variables: { visitorId, leadId, limit: 100 } })
        .then(res => {
          this.journey = res.data.visitorJourney || null;
          // expand the most recent session by default (usually the one that converted)
          this.$set(this.expanded, 1, true);
        })
        .catch(() => {
          this.dialog = false;
        });
    },

    toggle(index) {
      this.$set(this.expanded, index, !this.expanded[index]);
    },
    sessionSummary(s) {
      const parts = [];
      if (s.visits) parts.push(`${s.visits} visita${s.visits === 1 ? "" : "s"}`);
      if (s.searches) parts.push(`${s.searches} búsqueda${s.searches === 1 ? "" : "s"}`);
      if (s.inquiries) parts.push(`${s.inquiries} consulta${s.inquiries === 1 ? "" : "s"}`);
      return parts.join(" · ") || `${(s.events || []).length} eventos`;
    },
    fmtSessionSpan(s) {
      const d = this.fmtDate(s.startedAt);
      const a = this.fmtTime(s.startedAt);
      const b = this.fmtTime(s.endedAt);
      if (!a) return d;
      return a === b ? `${d} · ${a}` : `${d} · ${a}–${b}`;
    },
    fmtTime(s) {
      if (!s) return "";
      try {
        return new Date(s).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
      } catch (e) {
        return "";
      }
    },

    // visit steps drill into the property; inquiry steps open that inquiry's detail
    // directly (by id); searches aren't clickable
    isClickable(e) {
      if (!e) return false;
      if (e.kind === "visit") return !!e.propertyId;
      if (e.kind === "inquiry") return !!e.inquiryId;
      return false;
    },
    openEvent(e) {
      if (!this.isClickable(e)) return;
      this.dialog = false;
      if (e.kind === "inquiry") {
        this.$emit("open-inquiry", e.inquiryId);
      } else {
        this.$emit("open-property", { id: e.propertyId, fullAddress: e.propertyAddress, propertyType: e.propertyType });
      }
    },

    opLabel(o) {
      return OP_LABELS[o] || o;
    },
    catLabel(c) {
      return CAT_LABELS[c] || c;
    },
    deviceLabel(d) {
      return DEV_LABELS[d] || d || "—";
    },
    tierLabel(t) {
      return TIER_LABELS[t] || t || "—";
    },
    tierClass(t) {
      return t ? `vjd__tier--${t}` : "";
    },
    typeLabel(t) {
      if (!t) return "—";
      const k = String(t).toLowerCase();
      const key = "property.type." + k;
      const label = this.$t(key);
      return label === key ? k.charAt(0).toUpperCase() + k.slice(1) : label;
    },

    eventDetail(e) {
      if (!e) return "";
      if (e.kind === "visit") return SRC_LABELS[e.detail] || e.detail || "";
      return e.detail || "";
    },

    searchChips(meta) {
      if (!meta) return [];
      const f = [];
      if (meta.operation) f.push({ label: this.opLabel(meta.operation), cls: "vjd__chip--op" });
      (meta.types || []).forEach(t => f.push({ label: this.typeLabel(t), cls: "" }));
      if (meta.zone) f.push({ label: meta.zone, cls: "vjd__chip--zone" });
      if (meta.minPrice || meta.maxPrice) {
        const cur = meta.currency || "";
        const lo = meta.minPrice ? this.compact(meta.minPrice) : "0";
        const hi = meta.maxPrice ? this.compact(meta.maxPrice) : "∞";
        f.push({ label: `${cur} ${lo}–${hi}`.trim(), cls: "vjd__chip--price" });
      }
      if (meta.minBrooms) f.push({ label: `${meta.minBrooms}+ dorm.`, cls: "" });
      if (meta.minBaths) f.push({ label: `${meta.minBaths}+ baños`, cls: "" });
      if (meta.minSurface) f.push({ label: `${meta.minSurface}+ m²`, cls: "" });
      const c = f.length ? f : [{ label: "Todas las propiedades", cls: "vjd__chip--muted" }];
      if (meta.results != null) c.push({ label: `${meta.results} resultados`, cls: "vjd__chip--muted" });
      return c;
    },

    priceLabel(intent) {
      const price = intent && intent.price;
      if (!price) return "";
      const cur = Object.keys(price)[0];
      if (!cur) return "";
      const band = price[cur];
      if (!band || !band.typical) return "";
      return `${cur} ${this.compact(band.min)}–${this.compact(band.max)}`;
    },
    compact(n) {
      const v = Number(n) || 0;
      if (v >= 1000000) return (v / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
      if (v >= 1000) return Math.round(v / 1000) + "k";
      return String(v);
    },

    kindLabel(kind) {
      return { search: "Búsqueda", visit: "Visita", inquiry: "Consulta" }[kind] || kind;
    },
    truncate(s, n = 140) {
      if (!s) return "";
      return s.length > n ? s.slice(0, n) + "…" : s;
    },
    fmtDate(s) {
      if (!s) return "—";
      try {
        return new Date(s).toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
      } catch (e) {
        return "—";
      }
    },
    fmtDateTime(s) {
      if (!s) return "";
      try {
        return new Date(s).toLocaleString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
      } catch (e) {
        return "";
      }
    }
  }
};
</script>

<style scoped>
/* the white panel is the scroll container so the background always covers the timeline */
.vjd { background: #fff; border-radius: 14px; padding: 20px 22px; max-height: 86vh; overflow-y: auto; }
.vjd--loading { display: flex; justify-content: center; padding: 48px; }
.vjd__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.vjd__name { font-size: 18px; font-weight: 700; color: #1a1814; display: flex; align-items: center; gap: 8px; }
.vjd__meta { font-size: 13px; color: #756f64; margin-top: 4px; }
.vjd__badge { font-size: 10px; font-weight: 700; background: #e3f3e8; color: #2c6e49; padding: 1px 8px; border-radius: 999px; }
.vjd__tier { font-size: 10px; font-weight: 700; padding: 1px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; }
.vjd__tier--hot { background: #fdecea; color: #b52118; }
.vjd__tier--warm { background: #fdeecf; color: #8a5a00; }
.vjd__tier--cold { background: #eef1f4; color: #6a727b; }

.vjd__chips { display: flex; gap: 6px; flex-wrap: wrap; margin: 12px 0; }
.vjd__chips--ev { margin: 8px 0 0; }
.vjd__chip { font-size: 11px; font-weight: 600; background: #f1ece3; color: #6a645a; padding: 2px 9px; border-radius: 999px; }
.vjd__chip--op { background: #fdeecf; color: #8a5a00; }
.vjd__chip--zone { background: #e1ecff; color: #2353b8; }
.vjd__chip--price { background: #efe3f7; color: #6a3a8f; }
.vjd__chip--muted { background: #f1ece3; color: #a39a8c; }
.vjd__chip--dev { background: #e9eef2; color: #43596b; }

.vjd__summary { font-size: 12.5px; color: #756f64; padding: 10px 0 6px; border-top: 1px solid #ece7de; }

/* why — top lead-score signals */
.vjd__why { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 2px 0 4px; }
.vjd__whylabel { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #a39a8c; }
.vjd__wchip { font-size: 11.5px; color: #5b544a; background: #f4f1ea; border: 1px solid #ece7de; border-radius: 999px; padding: 2px 9px; }
.vjd__wchip b { color: #b52118; font-weight: 700; }

/* sessions */
.vjd__sessions { margin-top: 6px; }
.vjd__session { border-top: 1px solid #ece7de; }
.vjd__session:first-child { border-top: none; }
.vjd__shead {
  display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
  background: none; border: none; cursor: pointer; font: inherit; color: inherit;
  padding: 12px 0;
}
.vjd__schev { flex: none; color: #a39a8c; }
.vjd__stitle { font-size: 13.5px; font-weight: 700; color: #1a1814; }
.vjd__sdate { font-weight: 500; color: #a39a8c; font-size: 12px; }
.vjd__sconv { flex: none; font-size: 10px; font-weight: 700; background: #e3f3e8; color: #2c6e49; padding: 1px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; }
.vjd__sdev { flex: none; font-size: 10px; font-weight: 600; background: #e9eef2; color: #43596b; padding: 1px 8px; border-radius: 999px; }
.vjd__ssum { margin-left: auto; font-size: 11.5px; color: #a39a8c; text-align: right; }
.vjd__timeline { display: flex; flex-direction: column; gap: 0; padding: 2px 0 8px 24px; }
.vjd__event { display: flex; align-items: flex-start; gap: 12px; padding: 10px 0; }
.vjd__event--link { cursor: pointer; margin: 0 -10px; padding-left: 10px; padding-right: 10px; border-radius: 10px; transition: background 0.15s; }
.vjd__event--link:hover { background: #faf8f4; }
.vjd__event--link:hover .vjd__echev { color: #b52118; }
.vjd__echev { flex: none; align-self: center; color: #cdc4b5; }
.vjd__eprop { display: flex; align-items: center; gap: 5px; font-size: 12.5px; color: #56504a; font-weight: 600; margin-top: 4px; }
.vjd__edot { flex: none; width: 10px; height: 10px; border-radius: 50%; margin-top: 5px; background: #756f64; }
.vjd__event--search .vjd__edot { background: #b52118; }
.vjd__event--visit .vjd__edot { background: #c89b3c; }
.vjd__event--inquiry .vjd__edot { background: #2c6e49; }
.vjd__ebody { flex: 1 1 auto; min-width: 0; border-bottom: 1px solid #f1ece3; padding-bottom: 10px; }
.vjd__etop { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.vjd__ekind { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: #a39a8c; }
.vjd__ewhen { font-size: 11.5px; color: #a39a8c; }
.vjd__etitle { font-size: 14px; font-weight: 600; color: #1a1814; margin-top: 2px; }
.vjd__edetail { font-size: 12.5px; color: #756f64; margin-top: 2px; white-space: pre-line; }
.vjd__empty { border: 1px dashed #dcd4c6; border-radius: 12px; padding: 28px; text-align: center; color: #a39a8c; font-size: 14px; }
</style>
